import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import HiddenTipHint from "@/components/HiddenTipHint";

function pad(n) { return String(n).padStart(2, "0"); }

export default function ConfirmedShifts() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    api.get("/shift-periods").then((r) => {
      setPeriods(r.data);
      const pub = r.data.find((p) => p.status === "published") || r.data[0];
      if (pub) setPeriodId(pub.id);
    });
    api.get("/users/my-staff").then((r) => setUsers(r.data));
    api.get("/departments").then((r) => setDepartments(r.data));
  }, []);

  useEffect(() => {
    if (!periodId) return;
    api.get(`/shifts?shift_period_id=${periodId}`).then((r) => setShifts(r.data));
  }, [periodId]);

  const period = periods.find((p) => p.id === periodId);
  const days = period ? new Date(period.year, period.month, 0).getDate() : 0;
  const confirmed = shifts.filter((s) => s.status === "confirmed" || s.status === "changed");
  // Build matrix
  const usersSorted = users.filter((u) => u.role !== "admin").sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // 合計時間 計算 (HH:MM〜HH:MM → hours)
  const hoursOf = (s) => {
    if (!s?.start_time || !s?.end_time) return 0;
    const [sH, sM] = s.start_time.split(":").map(Number);
    const [eH, eM] = s.end_time.split(":").map(Number);
    let mins = (eH * 60 + eM) - (sH * 60 + sM);
    if (mins < 0) mins += 24 * 60; // 翌日跨ぎ対応
    return mins / 60;
  };
  // Per day totals
  const dayTotals = {};
  for (let d = 1; d <= days; d++) {
    const dateStr = period ? `${period.year}-${pad(period.month)}-${pad(d)}` : "";
    const list = confirmed.filter((s) => s.date === dateStr);
    dayTotals[d] = {
      hours: list.reduce((sum, s) => sum + hoursOf(s), 0),
      count: list.length,
    };
  }

  return (
    <div className="space-y-4" data-testid="confirmed-shifts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">確定シフト</h1>
          <div className="mt-2">
            <HiddenTipHint text="日毎に「希望に対して配置できたか」が見える" anchor="tip-day-coverage" />
          </div>
        </div>
        <Select value={periodId} onValueChange={setPeriodId}>
          <SelectTrigger className="w-56 bg-white" data-testid="confirmed-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {periods.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.year}年{p.month}月分</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="p-3 bg-white border border-slate-200 overflow-x-auto">
        {period && (
          <table className="text-xs w-full border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-slate-100 border border-slate-200 px-2 py-1 text-left z-10">氏名</th>
                {Array.from({ length: days }, (_, i) => i + 1).map((d) => (
                  <th key={d} className="border border-slate-200 px-1 py-1 min-w-[60px]">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usersSorted.map((u) => (
                <tr key={u.id} data-testid={`row-${u.id}`}>
                  <td className="sticky left-0 bg-white border border-slate-200 px-2 py-1 font-semibold whitespace-nowrap">
                    {u.name}
                  </td>
                  {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                    const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
                    const s = confirmed.find((x) => x.user_id === u.id && x.date === dateStr);
                    return (
                      <td key={d} className={`border border-slate-200 px-1 py-1 text-center ${s?.status === "changed" ? "bg-yellow-50" : s ? "bg-green-50" : ""}`}>
                        {s ? `${s.start_time}〜${s.end_time}` : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-bold" data-testid="footer-hours">
                <td className="sticky left-0 bg-slate-100 border border-slate-200 px-2 py-1 text-right">合計時間</td>
                {Array.from({ length: days }, (_, i) => i + 1).map((d) => (
                  <td key={d} className="border border-slate-200 px-1 py-1 text-center text-blue-700">
                    {dayTotals[d].hours > 0 ? `${dayTotals[d].hours}h` : "—"}
                  </td>
                ))}
              </tr>
              <tr className="bg-slate-50 font-bold" data-testid="footer-count">
                <td className="sticky left-0 bg-slate-100 border border-slate-200 px-2 py-1 text-right">出勤人数</td>
                {Array.from({ length: days }, (_, i) => i + 1).map((d) => (
                  <td key={d} className="border border-slate-200 px-1 py-1 text-center text-emerald-700">
                    {dayTotals[d].count > 0 ? `${dayTotals[d].count}名` : "—"}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        )}
      </Card>
    </div>
  );
}
